import { mapState, mapActions, mapMutations } from 'vuex'
import { apiPostValidateAuth } from '@/modules/User/services/authentication'
import { apiGetDamage } from '@/modules/Damage/services/damages'
import userLoginMixin from '@/modules/User/mixins/userLoginMixin'
import userFlowMixin from '@/modules/User/mixins/userFlowMixin'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import maNotifications from '@/components/notifications'
import maLoader from '@/components/loader'
import { USER_MODULE_NAME } from '@/modules/User/store/types'
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_GET_SINISTER,
  WARRANTY_GET_SINISTER_SUMMARY,
  WARRANTY_UPDATE_SINISTER_SUMMARY,
  FNOL_MODULE_NAME,
  FNOL_UPDATE_ID_FLUX
} from '@/store/types'
import maNavBar from './components/navBar'
import maButtonPhone from '@/components/buttonPhone'
import maEventBus from '@/components/eventbus/index.js'
import warrantyModule from '@/store/modules/warranty'
import billmaHttpClient from '@/services/http/billmaHttpClient'
import {
  setUserType,
  getUserType,
  isValidUserType
  //isPhotoclaim  //Remove Photoclaim on PRE
} from '@/utils/userSettings'

import * as Sentry from '@sentry/vue'

export default {
  name: 'App',

  mixins: [userLoginMixin, userFlowMixin, flowMixin],

  components: {
    maLoader,
    maNotifications,
    maNavBar,
    maButtonPhone
  },

  data: function () {
    return {
      appConfigIsLoaded: false
    }
  },

  computed: {
    ...mapState({
      stateLoader: (state) => state.loader,
      agentView: (state) => state.agentView
    }),
    ...mapState(USER_MODULE_NAME, {
      stateUserRoutes: 'routes'
    }),
    ...mapState(FNOL_MODULE_NAME, {
      stateIdFlux: 'idFlux'
    })
  },

  methods: {
    ...mapMutations(FNOL_MODULE_NAME, {
      mutFnolIdFlux: FNOL_UPDATE_ID_FLUX
    }),
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateSinisterSummary: WARRANTY_UPDATE_SINISTER_SUMMARY
    }),
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyGetSinister: WARRANTY_GET_SINISTER,
      actWarrantyGetSinisterSummary: WARRANTY_GET_SINISTER_SUMMARY
    }),

    matchLocale: function (locale) {
      const locales = this.$i18n.availableLocales
      if (locales.length <= 0) return
      return locales.indexOf(locale) !== -1 ? locale : locales[0]
    },

    initI18n: function (lang) {
      if (typeof Storage !== 'undefined') {
        let current
        if (lang) {
          current = this.matchLocale(lang)
        } else if (this.$route.query.lang) {
          current = this.matchLocale(this.$route.query.lang)
        } else if (localStorage.getItem('locale')) {
          current = this.matchLocale(localStorage.getItem('locale'))
        } else {
          current = 'fr'
        }
        this.$i18n.locale = current
        localStorage.setItem('locale', current)
      }
    },

    async checkSinisterData() {
      try {
        const userToken = sessionStorage.getItem('mavAuth')
        const sinId = sessionStorage.getItem('sinId')
        const formId = sessionStorage.getItem('formId')
        if (!userToken || !sinId || !formId) {
          // New session or bad request, clean and try fresh op
          localStorage.clear()
          const encSinisterId = this.$route.query.sinisterId
          const code = this.$route.query.code
          if (!encSinisterId && !code) {
            if (
              this.$route.name !== 'timedout' &&
              this.$route.name !== 'error'
            ) {
              this.$router.replace({
                name: 'error',
                query: { ...this.$route.query }
              })
            }
            return
          }
          else {
            if(code){
              const data = await apiPostValidateAuth(code)
              console.log('token ' + data.token)
              if (data.token) {
                this.$route.query.sinisterId = null
                sessionStorage.setItem('sinId',data.sinisterId)
                const formId = sessionStorage.setItem('formId',data.formId)
                sessionStorage.setItem('mavAuth', `Token ${data.token}`)
              }
            }
          }
        }
        // Logged user, send to inital screen
        await this.getUserData()
        this.$router.replace({
          name: 'claim-home',
          query: { ...this.$route.query }
        })
      } catch (e) {
        console.log(e)
        try {
          if (
            sessionStorage.getItem('otpSent') &&
            sessionStorage.getItem('otpSent') === 'true'
          ) {
            if (
              !sessionStorage.getItem('sinId') ||
              !sessionStorage.getItem('formId')
            ) {
              await this.getOtpStatus()
            }
          } else {
            sessionStorage.setItem('otpSent', true)
            await this.getOtpStatus()
          }
        } catch (e) {
          if (this.$route.name !== 'timedout') {
            this.$router.replace({
              name: 'timedout',
              query: { ...this.$route.query }
            })
          }
        }
      }
    },

    async getSinisterDamages() {
      if (!this.getSinisterId) return
      await apiGetDamage(this.getSinisterId).then((damages) => {
        this.mutWarrantyUpdateSinisterSummary(damages)
      })
    },

    async handleUserLogin() {
      localStorage.removeItem('mavAuth')
      try {
        return await this.sendLogin()
      } catch (e) {
        throw new Error(e)
      }
    },

    async handleFlowData() {
      if (!sessionStorage.getItem('mavAuth')) return
      try {
        const flowData = await this.getStateFlowData()
        const { flow, smartFluxId, lang, state, transitions, options } =
          flowData
        console.log(flowData)
        this.setFlowName(flow)
        this.setFlowState(state, transitions)
        if (options.content) this.setViewContent(options.content)
        this.setViewOptions(options)
        this.setFluxData(smartFluxId)
        this.initI18n(lang)

        return flowData
      } catch (e) {
        throw new Error(e)
      }
    },

    setFluxData(fluxId) {
      this.mutFnolIdFlux(fluxId)
    },

    checkScencrypt() {
      if (this.$route.name !== 'scencrypt') return

      const scencryptId = this.$route.params.scencryptId

      return billmaHttpClient.get(`/scencrypt/${scencryptId}`)
    },

    handleRedirectToAppEntrypoint() {
      if (
        this.$route.name !== 'otp' &&
        this.$route.name !== 'claim-home' &&
        this.$route.name !== 'error' &&
        this.$route.name !== 'timedout'
      ) {
        this.$router.replace({ name: 'otp', query: { ...this.$route.query } })
      }
    },

    handleUserTypeOnInit() {
      // We check if there is defined in the url. If not, we check if it is defined by previous session in session storage.
      let userTypeQueryParam = this.$route.query.user_type
      if (!userTypeQueryParam || !isValidUserType(userTypeQueryParam)) {
        userTypeQueryParam = getUserType()
        this.$router.replace({ query: { user_type: userTypeQueryParam } })
      }

      setUserType(userTypeQueryParam)

      //Remove Photoclaim PRE
      /* if (isPhotoclaim()) {
        this.setUserFlow('photoclaim')
      } */
    },

    emitScrollEvent() {
      maEventBus.$emit('onScroll')
    },

    initApp() {
      this.initI18n()
      this.checkScencrypt()
      this.handleUserTypeOnInit()
    },

    userCloseWindow() {
      window.onbeforeunload = function () {
        alert('vas a cerrar la ventana')
        // Send sentry custom event
        const scope = new Sentry.Scope()
        scope.setTag('section', 'App')
        scope.setTag({
          view: 'App.js'
        })
        Sentry.captureException(new Error('User close the window'), () => scope)
      }
    }
  },

  beforeCreate: function () {
    if (!(WARRANTY_MODULE_NAME in this.$store.state)) {
      this.$store.registerModule(WARRANTY_MODULE_NAME, warrantyModule)
    }
  },

  created: async function () {
    try {
      this.initApp()
      await this.checkSinisterData()
      this.handleRedirectToAppEntrypoint()
      this.appConfigIsLoaded = true
    } catch (e) {
      if (this.$route.name !== 'error') {
        this.$router.replace({ name: 'error', query: { ...this.$route.query } })
      }
      this.appConfigIsLoaded = true
    }

    // event bus for scrolling. This will be hear by components like selects
    window.addEventListener('scroll', this.emitScrollEvent)

    this.userCloseWindow()
  },

  beforeDestroy: function () {
    window.removeEventListener('scroll', this.emitScrollEvent)
    this.resetFlowState()
    sessionStorage.clear()
  }
}
